// ============================================================================
// 두벌식 (2-set) Hangul jamo composition engine (spec §7.4).
// ----------------------------------------------------------------------------
// Assembles 초성/중성/종성 jamo into syllable blocks in real time via the Unicode
// formula:  syllable = 0xAC00 + (cho×21 + jung)×28 + jong
// Handles compound vowels (ㅘ ㅙ ㅚ ㅝ ㅞ ㅟ ㅢ), double finals (ㄲ ㄳ ㄵ …),
// backspace decomposition, and re-composition when a vowel follows a final.
// Pure/portable: no DOM. The on-screen keyboard and native IME both feed this.
// ============================================================================

const S_BASE = 0xac00;
const N_JUNG = 21;
const N_JONG = 28;

// Jamo tables, indexed exactly as the Unicode formula requires.
const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const JUNG = ['ㅏ','ㅐ','ㅑ','ㅒ','ㅓ','ㅔ','ㅕ','ㅖ','ㅗ','ㅘ','ㅙ','ㅚ','ㅛ','ㅜ','ㅝ','ㅞ','ㅟ','ㅠ','ㅡ','ㅢ','ㅣ'];
const JONG = ['','ㄱ','ㄲ','ㄳ','ㄴ','ㄵ','ㄶ','ㄷ','ㄹ','ㄺ','ㄻ','ㄼ','ㄽ','ㄾ','ㄿ','ㅀ','ㅁ','ㅂ','ㅄ','ㅅ','ㅆ','ㅇ','ㅈ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];

const choIndex = (ch) => CHO.indexOf(ch);
const jungIndex = (ch) => JUNG.indexOf(ch);
const jongIndex = (ch) => JONG.indexOf(ch);
const isVowelJamo = (ch) => JUNG.includes(ch);
const isConsonantJamo = (ch) => CHO.includes(ch) || JONG.includes(ch);

// Compound-vowel assembly and its inverse (by JUNG index).
const VOWEL_COMBINE = { '8,0':9, '8,1':10, '8,20':11, '13,4':14, '13,5':15, '13,20':16, '18,20':19 };
const VOWEL_SPLIT = { 9:[8,0], 10:[8,1], 11:[8,20], 14:[13,4], 15:[13,5], 16:[13,20], 19:[18,20] };

// Compound-final assembly (baseJongIndex + added consonant char) and its inverse.
const FINAL_COMBINE = {
  '1,ㅅ':3, '4,ㅈ':5, '4,ㅎ':6, '8,ㄱ':9, '8,ㅁ':10, '8,ㅂ':11,
  '8,ㅅ':12, '8,ㅌ':13, '8,ㅍ':14, '8,ㅎ':15, '17,ㅅ':18,
};
const FINAL_SPLIT = {
  3:[1,'ㅅ'], 5:[4,'ㅈ'], 6:[4,'ㅎ'], 9:[8,'ㄱ'], 10:[8,'ㅁ'], 11:[8,'ㅂ'],
  12:[8,'ㅅ'], 13:[8,'ㅌ'], 14:[8,'ㅍ'], 15:[8,'ㅎ'], 18:[17,'ㅅ'],
};

const EMPTY = () => ({ cho: -1, jung: -1, jong: -1 });

/** Decompose a precomposed syllable char code into jamo indices, or null. */
function decomposeSyllable(code) {
  if (code < S_BASE || code > 0xd7a3) return null;
  const s = code - S_BASE;
  return {
    cho: Math.floor(s / (N_JUNG * N_JONG)),
    jung: Math.floor((s % (N_JUNG * N_JONG)) / N_JONG),
    jong: s % N_JONG,
  };
}

/**
 * Stateful 두벌식 composer. `committed` holds finished text; `cur` holds the
 * in-progress syllable. `.value` is what the shared input field should show.
 */
export class HangulComposer {
  constructor(initial = '') {
    this.committed = initial;
    this.cur = EMPTY();
  }

  reset(initial = '') {
    this.committed = initial;
    this.cur = EMPTY();
    return this;
  }

  get value() {
    return this.committed + this._render(this.cur);
  }

  _render(c) {
    if (c.cho < 0 && c.jung < 0) return '';
    if (c.cho >= 0 && c.jung < 0) return CHO[c.cho];          // lone initial
    if (c.cho < 0 && c.jung >= 0) return JUNG[c.jung];        // lone vowel
    const jong = c.jong < 0 ? 0 : c.jong;
    return String.fromCharCode(S_BASE + (c.cho * N_JUNG + c.jung) * N_JONG + jong);
  }

  _commit() {
    this.committed += this._render(this.cur);
    this.cur = EMPTY();
  }

  /** Feed one jamo (from the on-screen keyboard or a decomposed IME keystroke). */
  input(jamo) {
    if (isVowelJamo(jamo)) return this._inputVowel(jamo), this;
    if (isConsonantJamo(jamo)) return this._inputConsonant(jamo), this;
    // Any non-jamo (space, punctuation, latin) flushes and appends verbatim.
    this._commit();
    this.committed += jamo;
    return this;
  }

  _inputConsonant(ch) {
    const c = this.cur;
    const asCho = choIndex(ch);
    const asJong = jongIndex(ch);

    if (c.cho < 0 && c.jung < 0) {           // empty → start an initial
      c.cho = asCho >= 0 ? asCho : c.cho;
      if (asCho < 0) { this.committed += ch; } // consonant with no cho form
      return;
    }
    if (c.cho >= 0 && c.jung < 0) {          // lone initial + consonant → split
      this._commit();
      this.cur.cho = asCho;
      return;
    }
    if (c.jong < 0) {                        // CV → try to add a final
      if (asJong > 0) { c.jong = asJong; return; }
      this._commit(); this.cur.cho = asCho; return;
    }
    // CVC → try to grow the final into a compound, else start a new syllable.
    const combined = FINAL_COMBINE[`${c.jong},${ch}`];
    if (combined) { c.jong = combined; return; }
    this._commit();
    this.cur.cho = asCho;
  }

  _inputVowel(ch) {
    const c = this.cur;
    const v = jungIndex(ch);

    if (c.cho < 0 && c.jung < 0) { c.jung = v; return; }   // lone vowel
    if (c.jung < 0) { c.jung = v; return; }                // initial + vowel → CV

    if (c.jong < 0) {
      // CV + vowel → compound vowel if legal, else a new lone-vowel block.
      const combined = VOWEL_COMBINE[`${c.jung},${v}`];
      if (combined != null) { c.jung = combined; return; }
      this._commit(); this.cur.jung = v; return;
    }

    // CVC + vowel → re-composition: the final (or its tail) becomes a new initial.
    const split = FINAL_SPLIT[c.jong];
    if (split) {
      const [baseJong, moved] = split;
      c.jong = baseJong;
      this._commit();
      this.cur.cho = choIndex(moved);
      this.cur.jung = v;
    } else {
      const moved = JONG[c.jong];
      c.jong = 0;
      this._commit();
      this.cur.cho = choIndex(moved);
      this.cur.jung = v;
    }
  }

  /** Delete one jamo, decomposing compounds; pulls the last committed syllable
   *  back into the buffer so repeated backspaces peel jamo cleanly. */
  backspace() {
    const c = this.cur;
    if (c.cho < 0 && c.jung < 0) {
      if (!this.committed) return this;
      const last = this.committed.charCodeAt(this.committed.length - 1);
      const d = decomposeSyllable(last);
      this.committed = this.committed.slice(0, -1);
      if (d) this.cur = { cho: d.cho, jung: d.jung, jong: d.jong };
      return this;
    }
    if (c.jong > 0) {
      const fs = FINAL_SPLIT[c.jong];   // compound final → revert to its base part
      c.jong = fs ? fs[0] : 0;          // simple final → remove it
      return this;
    }
    if (c.jung >= 0) {
      const vs = VOWEL_SPLIT[c.jung];
      c.jung = vs ? vs[0] : -1;
      return this;
    }
    if (c.cho >= 0) c.cho = -1;
    return this;
  }
}

/** One-shot helper: compose a flat jamo sequence into text. */
export function composeJamo(jamoSequence) {
  const comp = new HangulComposer();
  for (const j of jamoSequence) comp.input(j);
  return comp.value;
}

export const JAMO = { CHO, JUNG, JONG };
